"use client"

import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Link from "next/link";
import ReCAPTCHA from 'react-google-recaptcha';

import PAGES from "@/constants/pages";
import { uuid } from "@/utils";

const websiteKey = process?.env?.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
const formState = {
  name: '',
  email: '',
  message: '',
  company: '',
  phone: ''
};

const Contact = () => {
  const intl = useIntl();

  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [status, setStatus] = useState(null);
  const [formData, setFormData] = useState(formState);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onCaptchaChange = (value) => {
    // This will be triggered when the reCAPTCHA is verified
    if (value) {
      setCaptchaVerified(value);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!captchaVerified) {
      setStatus('Please complete the reCAPTCHA');
      return;
    }

    setStatus('Sending...');
    try {
      const res = await fetch('/api/website-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          captchaToken: captchaVerified
        }),
      });

      if (res.ok) {
        setStatus('Message sent!');
        setFormData(formState);
      } else {
        const { error } = await res.json();
        setStatus(error);
      }
    } catch (err) {
      setStatus('Error sending message.');
    }
  };

  return (
    <section id="support" className="pt-[100px] pb-[110px]">
      <div className="container">
        <div className="wow fadeInUp mx-auto mb-10 max-w-[690px] text-center" data-wow-delay=".2s">
          <h2 className="mb-4 text-3xl font-bold text-black dark:text-white sm:text-4xl md:text-[44px] md:leading-tight">
            <FormattedMessage
              id="home-page-contact-title"
            />
          </h2>
          <p className="text-base text-body">
            <FormattedMessage
              id="home-page-contact-description"
            />
          </p>
        </div>
      </div>

      <div className="container">
        <div className="wow fadeInUp mx-auto w-full max-w-[925px] rounded-lg bg-[#F8FAFB] px-8 py-10 shadow-card dark:bg-[#15182B] dark:shadow-card-dark sm:px-10" data-wow-delay=".3s">
          <form
            onSubmit={handleSubmit}
          >
            <div className="-mx-[22px] flex flex-wrap">
              <div className="w-full px-[22px] md:w-1/2">
                <div className="mb-8">
                  <input
                    type="text"
                    name="name"
                    id="name"
                    required
                    value={formData?.name}
                    onChange={handleChange}
                    placeholder={intl.formatMessage({ id: "home-page-contact-form-input-name-placeholder" })}
                    className="w-full rounded-sm border border-stroke bg-white py-4 px-[30px] text-base text-body outline-hidden focus:border-primary dark:border-[#34374A] dark:bg-[#2A2E44] dark:focus:border-primary"
                  />
                </div>
              </div>

              <div className="w-full px-[22px] md:w-1/2">
                <div className="mb-8">
                  <input
                    type="text"
                    name="company"
                    id="company"
                    value={formData?.company}
                    onChange={handleChange}
                    placeholder={intl.formatMessage({ id: "home-page-contact-form-input-company-placeholder" })}
                    className="w-full rounded-sm border border-stroke bg-white py-4 px-[30px] text-base text-body outline-hidden focus:border-primary dark:border-[#34374A] dark:bg-[#2A2E44] dark:focus:border-primary"
                  />
                </div>
              </div>

              <div className="w-full px-[22px] md:w-1/2">
                <div className="mb-8">
                  <input
                    type="email"
                    name="email"
                    required
                    id="email"
                    value={formData?.email}
                    onChange={handleChange}
                    placeholder={intl.formatMessage({ id: "home-page-contact-form-input-email-placeholder" })}
                    className="w-full rounded-sm border border-stroke bg-white py-4 px-[30px] text-base text-body outline-hidden focus:border-primary dark:border-[#34374A] dark:bg-[#2A2E44] dark:focus:border-primary"
                  />
                </div>
              </div>

              <div className="w-full px-[22px] md:w-1/2">
                <div className="mb-8">
                  <input
                    type="text"
                    name="phone"
                    id="phone"
                    value={formData?.phone}
                    onChange={handleChange}
                    placeholder={intl.formatMessage({ id: "home-page-contact-form-input-phone-placeholder" })}
                    className="w-full rounded-sm border border-stroke bg-white py-4 px-[30px] text-base text-body outline-hidden focus:border-primary dark:border-[#34374A] dark:bg-[#2A2E44] dark:focus:border-primary"
                  />
                </div>
              </div>

              <div className="w-full px-[22px]">
                <div className="mb-8">
                  <textarea
                    rows="6"
                    required
                    name="message"
                    id="message"
                    value={formData?.message}
                    onChange={handleChange}
                    placeholder={intl.formatMessage({ id: "home-page-contact-form-input-message-placeholder" })}
                    className="w-full rounded-sm border border-stroke bg-white py-4 px-[30px] text-base text-body outline-hidden focus:border-primary dark:border-[#34374A] dark:bg-[#2A2E44] dark:focus:border-primary"
                  ></textarea>
                </div>
              </div>

              <div className="w-full px-[22px]">
                <div className="text-center">
                  <p className="mb-5 text-base text-center text-body">
                    <FormattedMessage
                      id="home-page-contact-terms-and-policy-description"
                      values={{
                        linkOne: (chunks) => (
                          <Link
                            key={uuid()}
                            title="Terms of Service link"
                            href={PAGES.termsOfService}
                            className="text-primary font-bold"
                          >
                            {chunks}
                          </Link>
                        ),
                        linkTwo: (chunks) => (
                          <Link
                            key={uuid()}
                            title="Privacy Policy link"
                            href={PAGES.privacyPolicy}
                            className="text-primary font-bold"
                          >
                            {chunks}
                          </Link>
                        )
                      }}
                    />
                  </p>
                  <ReCAPTCHA
                    sitekey={websiteKey}
                    onChange={onCaptchaChange}
                    className="flex items-center justify-center mb-4"
                  />
                  <button
                    type="submit"
                    disabled={(status === 'Sending...')}
                    className="inline-block rounded-md disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500 disabled:shadow-none bg-primary py-[14px] px-11 text-base font-bold text-white hover:bg-primary/90"
                  >
                    <FormattedMessage
                      id="home-page-contact-submit-button"
                    />
                  </button>
                </div>
              </div>
            </div>
          </form>
          {status && <p className="mt-4 p-4 text-center stroke-sky-500">{status}</p>}
        </div>
      </div>
    </section>
  );
}

export default Contact;